import { createApp } from '@backstage/frontend-defaults';
import { navModule } from './modules/nav';
import homePlugin from '@backstage/plugin-home/alpha';
import { catalogPluginWithOverrides } from './modules/catalog';
import { apiDocsPluginWithOverrides } from './modules/api-docs';
import { techdocsPluginWithOverrides } from './modules/techdocs';
import { githubActionsPluginWithOverrides } from './modules/github-actions';
import { datadogPlugin } from './modules/datadog';
import { catalogGraphPluginWithOverrides } from './modules/catalog-graph';
import { bcmPlugin } from './modules/bcm';
import { ossLicensesPlugin } from './modules/oss-licenses';
import { workMixPlugin } from './modules/work-mix';
import { knowledgeCoveragePlugin } from './modules/knowledgeCoverage';
import { searchPluginWithOverrides } from './modules/search';
import {
  SignInPageBlueprint,
  ThemeBlueprint,
} from '@backstage/plugin-app-react';
import { AppRootElementBlueprint } from '@backstage/frontend-plugin-api';
import { ExternalUserProbe } from './modules/externalUser';
import { datadogAnalyticsApi } from './modules/analytics';
import { orgModule } from './modules/org';
import { SignInPage } from '@backstage/core-components';
import { UnifiedThemeProvider } from '@backstage/theme';
import { createFrontendModule } from '@backstage/frontend-plugin-api';
import {
  savrDarkGlobalCss,
  savrDarkTheme,
  savrLightGlobalCss,
  savrLightTheme,
} from './theme/savrTheme';
import { SavrSignInPage } from './components/SavrSignInPage';

// Default the sidebar to collapsed (icons only, expands on hover). The stock
// default is pinned open, and SidebarPage persists whatever state it mounted
// with into localStorage on every visit - so this is a one-time migration:
// everyone gets the collapsed default once, and re-pinning it from Settings
// afterwards sticks. Must run before the app mounts SidebarPage.
try {
  if (window.localStorage.getItem('savr.sidebarCollapsedDefault') === null) {
    window.localStorage.setItem('sidebarPinState', 'false');
    window.localStorage.setItem('savr.sidebarCollapsedDefault', '1');
  }
} catch {
  /* storage unavailable (e.g. blocked) - keep the stock default */
}

// Local dev (`yarn start`, NODE_ENV=development) signs in as guest so there's no
// IdP round-trip. Built/deployed bundles use the branded SAVR sign-in page with
// Google (internal) and GitHub (external partners, see org/external-users.yaml).
const signInPage = SignInPageBlueprint.make({
  params: {
    loader: async () => props =>
      process.env.NODE_ENV === 'development' ? (
        <SignInPage {...props} providers={['guest']} />
      ) : (
        <SavrSignInPage {...props} />
      ),
  },
});

const savrDark = ThemeBlueprint.make({
  name: 'savr-dark',
  params: {
    theme: {
      id: 'savr-dark',
      title: 'SAVR Dark',
      variant: 'dark',
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={savrDarkTheme}>
          {/* Fonts + global CSS; MuiCssBaseline overrides are silently
              dropped by the unified theme adapter (see savrTheme.ts). */}
          <style>{savrDarkGlobalCss}</style>
          {children}
        </UnifiedThemeProvider>
      ),
    },
  },
});

const savrLight = ThemeBlueprint.make({
  name: 'savr-light',
  params: {
    theme: {
      id: 'savr-light',
      title: 'SAVR Light',
      variant: 'light',
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={savrLightTheme}>
          <style>{savrLightGlobalCss}</style>
          {children}
        </UnifiedThemeProvider>
      ),
    },
  },
});

export default createApp({
  features: [
    homePlugin,
    // Registered before the catalog plugin so the Service health card lands
    // at the top of the Overview info column, above About.
    datadogPlugin,
    catalogPluginWithOverrides,
    apiDocsPluginWithOverrides,
    techdocsPluginWithOverrides,
    githubActionsPluginWithOverrides,
    // Before catalog-graph so a process page leads with its Flow and Support
    // cards rather than the Relations graph: on a BusinessProcess the flow is
    // the point, and the topology is the supporting detail. Cards elsewhere
    // are unaffected - these are filtered to the BusinessProcess kind.
    bcmPlugin,
    // Licenses tab + Overview card on services, and the /licenses inventory.
    ossLicensesPlugin,
    // After bcm and before catalog-graph: on a component page the work mix is
    // context for whoever has landed there, not the headline, so it sits
    // below health and above the relations graph.
    workMixPlugin,
    knowledgeCoveragePlugin,
    catalogGraphPluginWithOverrides,
    searchPluginWithOverrides,
    navModule,
    orgModule,
    createFrontendModule({
      pluginId: 'app',
      extensions: [
        signInPage,
        savrDark,
        savrLight,
        // Portal usage metrics -> savr.backstage.* in Datadog.
        datadogAnalyticsApi,
        // Warms the external-user cache used by entity tab filters.
        AppRootElementBlueprint.make({
          name: 'external-user-probe',
          params: { element: <ExternalUserProbe /> },
        }),
      ],
    }),
  ],
});
